<script lang="ts" setup>
import { mdiMenu as mdiMenu } from "@mdi/js";
import { reactive } from "vue";
import { useRouter } from "vue-router";
import { useDisplay } from "vuetify";

import { useAppTheme } from "#composables/use-app-theme";
import { useDialog } from "#composables/use-dialog";
import { appConfig } from "#config";
import { useAuthAccount } from "#modules/account/account.store";
import { routeNames } from "#router/constants";

const emit = defineEmits<{
  // eslint-disable-next-line @typescript-eslint/prefer-function-type
  (e: "toggle-drawer"): void;
}>();

const { dark, icon: themeIcon, toggleTheme } = useAppTheme();
const { mobile } = useDisplay();
const router = useRouter();

const accountStore = reactive(useAuthAccount());
const logoutConfirmDialog = useDialog();

const handleLogout = async () => {
  // Logout functionality is handled on logout page
  await router.push("/auth/logout");
  logoutConfirmDialog.hide();
};
</script>

<template>
  <VAppBar :color="!dark ? 'primary' : undefined" elevation="2">
    <template #prepend>
      <VBtn :active="false" icon :to="accountStore.authenticated ? '/dashboard' : '/'">
        <VAvatar class="pa-1" image="/logo.png" style="filter: brightness(0) invert(1)" />
      </VBtn>
    </template>

    <template #title>
      <VAppBarTitle text="My Choir Music" />
    </template>

    <template #append>
      <template v-if="appConfig.development && !mobile">
        <VBtn density="comfortable" href="http://localhost:8025/#" icon target="_blank">
          <template #default>
            <img
              alt="MailHog logo"
              class="opacity-60"
              src="/logo-mailhog.png"
              style="filter: brightness(0) invert(1)"
              width="24"
            />
          </template>
        </VBtn>
        <VBtn density="comfortable" :href="`${appConfig.links.api}/_/`" icon target="_blank">
          <template #default>
            <img alt="Pocketbase logo" class="opacity-60" src="/logo-pocketbase.png" width="20" />
          </template>
        </VBtn>
      </template>

      <VDivider v-if="!mobile && appConfig.development" class="my-4 mx-1" vertical />
      <template v-if="!mobile">
        <VBtn density="comfortable" :icon="themeIcon" @click="toggleTheme" />
      </template>
      <template v-if="accountStore.account">
        <VMenu>
          <template #activator="{ props }">
            <VBtn v-bind="props" class="mx-1" icon>
              <VAvatar color="white">
                {{ accountStore.account.name.charAt(0).toUpperCase() }}
              </VAvatar>
            </VBtn>
          </template>
          <VList class="mt-1" min-width="200">
            <VListItem class="text-subtitle-2">{{ accountStore.account.name }}</VListItem>
            <VDivider />
            <VListItem :to="{ name: routeNames.auth.profile }">Profile</VListItem>
            <VListItem @click="logoutConfirmDialog.show">Logout</VListItem>
          </VList>
        </VMenu>
      </template>
      <template v-else>
        <VBtn :active="false" :to="{ name: routeNames.auth.signup }" variant="outlined"
          >Signup</VBtn
        >
        <VBtn
          :active="false"
          :to="{ name: routeNames.auth.login }"
          :variant="dark ? 'outlined' : 'elevated'"
        >
          Login
        </VBtn>
      </template>
      <VBtn
        v-if="mobile && accountStore.authenticated"
        density="comfortable"
        :icon="mdiMenu"
        @click="emit('toggle-drawer')"
      />
    </template>

    <ConfirmDialog v-model="logoutConfirmDialog.open.value" title="Logout?" @confirm="handleLogout">
      Are you sure you want to logout?
    </ConfirmDialog>
  </VAppBar>
</template>

<style lang="scss" scoped>
:deep(.v-toolbar__append) {
  gap: spacing(2);
}
</style>
